import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDef<T extends string> {
  id: T;
  label: string;
  badge?: ReactNode;
}

/** WAI-ARIA tabs: roving tabindex, arrows/Home/End move focus and select. */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  idPrefix,
}: {
  tabs: TabDef<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  idPrefix: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = -1;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next >= 0) {
      e.preventDefault();
      onChange(tabs[next].id);
      refs.current[next]?.focus();
    }
  };
  return (
    <div role="tablist" aria-label={ariaLabel} className="flex shrink-0 items-end gap-1 overflow-x-auto overscroll-x-contain border-b border-line bg-bg px-4">
      {tabs.map((tab, i) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            data-state={selected ? 'active' : 'inactive'}
            // No transition on the indicator: it must switch in the same frame as aria-selected.
            className={`-mb-px inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-semibold ${
              selected ? 'is-active border-fg text-fg' : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ idPrefix, id, active, children }: { idPrefix: string; id: string; active: boolean; children: ReactNode }) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      hidden={!active}
      tabIndex={0}
      // relative: contains absolutely positioned descendants (sr-only) inside the scrolled panel.
      className="relative min-h-0 flex-1 overflow-auto focus-visible:outline-offset-[-2px]"
    >
      {active ? children : null}
    </div>
  );
}
